import { useEffect, useState, useMemo, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { X, ChevronDown, Check } from "@credcore/core-ui/icons";
import { Button } from "@/components/shadcn-components/Button";
import { Input } from "@/components/shadcn-components/Input";
import { DatePicker } from "@/components/shadcn-components/DatePicker/DatePicker";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/shadcn-components/Popover";
import {
  useFilterOptionsQuery,
  ClassificationType,
} from "@/graphql/types/models";
import { cn } from "@/lib/utils";
import { useOrg } from "@/contexts/OrgContext";
import { daysAgo } from "@/lib/constants";

interface FilterPanelProps {
  role?: "ANALYST" | "REVIEWER";
  onFilterChange?: (filters: FilterState) => void;
  className?: string;
}

export interface FilterState {
  dateRange: {
    from?: Date;
    to?: Date;
  };
  personIds: string[];
  classifications: string[];
  minDocuments?: number;
}

const PRESET_RANGES = [
  { label: "7d", value: "7d" },
  { label: "30d", value: "30d" },
  { label: "90d", value: "90d" },
];

export function FilterPanel({
  role,
  onFilterChange,
  className,
}: FilterPanelProps) {
  const [searchParams, setSearchParams] = useSearchParams();
  const { orgId } = useOrg();
  const onFilterChangeRef = useRef(onFilterChange);
  onFilterChangeRef.current = onFilterChange;

  const [filters, setFilters] = useState<FilterState>(() => {
    const fromDate = searchParams.get("from");
    const toDate = searchParams.get("to");
    const persons =
      searchParams.get("persons")?.split(",").filter(Boolean) || [];
    const classifications =
      searchParams.get("classifications")?.split(",").filter(Boolean) || [];

    const minDocs = searchParams.get("minDocs");

    return {
      dateRange: {
        from: fromDate ? new Date(fromDate) : daysAgo(7),
        to: toDate ? new Date(toDate) : new Date(),
      },
      personIds: persons,
      classifications,
      minDocuments: minDocs ? parseInt(minDocs, 10) : undefined,
    };
  });

  const [personSearch, setPersonSearch] = useState("");

  const { data: optionsData } = useFilterOptionsQuery({
    variables: { orgId },
  });

  const persons = useMemo(() => {
    if (role === "ANALYST") {
      return optionsData?.filterOptions?.analysts || [];
    } else if (role === "REVIEWER") {
      return optionsData?.filterOptions?.reviewers || [];
    }
    return [
      ...(optionsData?.filterOptions?.analysts || []),
      ...(optionsData?.filterOptions?.reviewers || []),
    ];
  }, [optionsData, role]);

  const filteredPersons = useMemo(() => {
    if (!personSearch) return persons.slice(0, 20);
    return persons
      .filter((p) => p.name.toLowerCase().includes(personSearch.toLowerCase()))
      .slice(0, 20);
  }, [persons, personSearch]);

  useEffect(() => {
    const params = new URLSearchParams();

    if (filters.dateRange.from) {
      params.set("from", filters.dateRange.from.toISOString());
    }
    if (filters.dateRange.to) {
      params.set("to", filters.dateRange.to.toISOString());
    }
    if (filters.personIds.length > 0) {
      params.set("persons", filters.personIds.join(","));
    }
    if (filters.classifications.length > 0) {
      params.set("classifications", filters.classifications.join(","));
    }
    if (filters.minDocuments) {
      params.set("minDocs", String(filters.minDocuments));
    }

    setSearchParams(params);
    onFilterChangeRef.current?.(filters);
  }, [filters, setSearchParams]);

  const handlePresetRange = (preset: string) => {
    let from: Date;

    switch (preset) {
      case "7d":
        from = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000);
        break;
      case "30d":
        from = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000);
        break;
      case "90d":
        from = new Date(Date.now() - 90 * 24 * 60 * 60 * 1000);
        break;
      default:
        from = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000);
    }

    setFilters((prev) => ({
      ...prev,
      dateRange: { from, to: new Date() },
    }));
  };

  const handleClearFilters = () => {
    const defaultFilters: FilterState = {
      dateRange: {
        from: daysAgo(7),
        to: new Date(),
      },
      personIds: [],
      classifications: [],
      minDocuments: undefined,
    };
    setFilters(defaultFilters);
  };

  const togglePerson = (id: string) => {
    setFilters((prev) => ({
      ...prev,
      personIds: prev.personIds.includes(id)
        ? prev.personIds.filter((i) => i !== id)
        : [...prev.personIds, id],
    }));
  };

  const toggleClassification = (classification: ClassificationType) => {
    setFilters((prev) => ({
      ...prev,
      classifications: prev.classifications.includes(classification)
        ? prev.classifications.filter((c) => c !== classification)
        : [...prev.classifications, classification],
    }));
  };

  const hasActiveFilters =
    filters.personIds.length > 0 ||
    filters.classifications.length > 0 ||
    filters.minDocuments !== undefined;

  const getRoleLabel = () => {
    if (role === "ANALYST") return "Analysts";
    if (role === "REVIEWER") return "Reviewers";
    return "Users";
  };

  const getSelectedPersonNames = (): string => {
    if (filters.personIds.length === 0) return `All ${getRoleLabel()}`;
    if (filters.personIds.length === 1) {
      return (
        persons.find((p) => p.id === filters.personIds[0])?.name ||
        filters.personIds[0]
      );
    }
    return `${filters.personIds.length} ${getRoleLabel()}`;
  };

  const classifications = (
    optionsData?.filterOptions?.classifications || []
  ).filter((c) => c !== ClassificationType.CORRECT);

  return (
    <div
      className={cn(
        "overflow-hidden rounded-lg bg-white p-4 shadow",
        className,
      )}
    >
      <div className="flex flex-wrap items-center gap-2">
        <div className="flex items-center gap-1 rounded-md border p-1">
          {PRESET_RANGES.map((preset) => (
            <button
              key={preset.value}
              onClick={() => handlePresetRange(preset.value)}
              className="rounded px-2 py-1 text-xs text-gray-600 hover:bg-gray-100"
            >
              {preset.label}
            </button>
          ))}
        </div>

        <div className="flex items-center gap-1">
          <DatePicker
            date={filters.dateRange.from}
            onDateChange={(date) =>
              setFilters((prev) => ({
                ...prev,
                dateRange: { ...prev.dateRange, from: date },
              }))
            }
            placeholder="From"
            compact
          />
          <span className="text-gray-400">-</span>
          <DatePicker
            date={filters.dateRange.to}
            onDateChange={(date) =>
              setFilters((prev) => ({
                ...prev,
                dateRange: { ...prev.dateRange, to: date },
              }))
            }
            placeholder="To"
            compact
          />
        </div>

        <Popover>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              size="sm"
              className="max-w-[180px] justify-between font-normal"
            >
              <span className="truncate">{getSelectedPersonNames()}</span>
              <ChevronDown className="ml-1 h-4 w-4 shrink-0 opacity-50" />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-64 p-0" align="start">
            <div className="p-2">
              <Input
                placeholder="Search..."
                value={personSearch}
                onChange={(e) => setPersonSearch(e.target.value)}
                className="mb-2"
              />
              <div className="max-h-48 overflow-y-auto">
                {filteredPersons.length === 0 ? (
                  <p className="p-2 text-center text-sm text-gray-500">
                    No results
                  </p>
                ) : (
                  filteredPersons.map((person) => (
                    <div
                      key={person.id}
                      onClick={() => togglePerson(person.id)}
                      className="flex cursor-pointer items-center justify-between rounded px-2 py-1.5 hover:bg-gray-100"
                    >
                      <span className="truncate text-sm">{person.name}</span>
                      {filters.personIds.includes(person.id) && (
                        <Check className="h-4 w-4 shrink-0 text-gray-700" />
                      )}
                    </div>
                  ))
                )}
              </div>
            </div>
          </PopoverContent>
        </Popover>

        <div className="flex items-center gap-1">
          <span className="text-xs text-gray-500">Min docs:</span>
          <Input
            type="number"
            min={0}
            value={filters.minDocuments ?? ""}
            onChange={(e) =>
              setFilters((prev) => ({
                ...prev,
                minDocuments: e.target.value
                  ? parseInt(e.target.value, 10)
                  : undefined,
              }))
            }
            className="h-8 w-16 text-xs"
            placeholder="0"
          />
        </div>

        {classifications.length > 0 && (
          <Popover>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                size="sm"
                className="max-w-[150px] justify-between font-normal"
              >
                <span className="truncate">
                  {filters.classifications.length === 0
                    ? "Error Type"
                    : `${filters.classifications.length} types`}
                </span>
                <ChevronDown className="ml-1 h-4 w-4 shrink-0 opacity-50" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-48 p-0" align="start">
              <div className="p-2">
                <div className="space-y-1">
                  {classifications.map((classification) => (
                    <div
                      key={classification}
                      onClick={() => toggleClassification(classification)}
                      className="flex cursor-pointer items-center justify-between rounded px-2 py-1.5 hover:bg-gray-100"
                    >
                      <span className="text-sm">
                        {classification.replace(/_/g, " ")}
                      </span>
                      {filters.classifications.includes(classification) && (
                        <Check className="h-4 w-4 shrink-0 text-gray-700" />
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </PopoverContent>
          </Popover>
        )}

        {hasActiveFilters && (
          <Button
            variant="ghost"
            size="sm"
            onClick={handleClearFilters}
            className="text-xs text-gray-500"
          >
            <X className="mr-1 h-3 w-3" />
            Clear
          </Button>
        )}
      </div>
    </div>
  );
}
